import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { User } from '@/api/types';
import { SubmitError } from '@/app/components/SubmitError';
import { Alert } from '@/app/components/ui/Alert';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Field, FormActions, Input } from '@/app/components/ui/Field';
import { updateMyProfile } from '@/features/profile/api';
import { useSubmit } from '@/lib/useSubmit';

/**
 * Name, nickname and the private fields (§ Accountmenu › 1). `name`/`email`
 * are read-only — synced from Keycloak, never editable here (same rule
 * `UserEditPage` follows). Preferred language, region and the statistics
 * timezone live on the Voorkeuren tab instead (`PreferencesPage`) — this
 * form still sends their current, unchanged values on every save, since `PUT
 * /users/{userId}` writes every field it is sent as `null` rather than
 * leaving it alone (the endpoint has no partial-update mode).
 */
export function ProfileDetailsCard({
  user,
  roles,
}: {
  user: User;
  /** Role badges (admin/company admin/department admin), shown between the
   * general and private fields — empty when the caller has no such role. */
  roles: string[];
}) {
  const { t } = useTranslation();

  const [nickname, setNickname] = useState(user.nickname ?? '');
  const [gender, setGender] = useState(user.gender != null ? String(user.gender) : '');
  const [birthdate, setBirthdate] = useState(user.birthdate ?? '');
  const [height, setHeight] = useState(user.height != null ? String(user.height) : '');
  const [weight, setWeight] = useState(user.weight != null ? String(user.weight) : '');
  const [saved, setSaved] = useState(false);

  const blank = (value: string) => value.trim() === '';

  const form = useSubmit(async () => {
    setSaved(false);
    await updateMyProfile({
      nickname: blank(nickname) ? null : nickname,
      preferred_language: user.preferred_language ?? null,
      region: user.region ?? null,
      gender: blank(gender) ? null : Number(gender),
      birthdate: blank(birthdate) ? null : birthdate,
      height: blank(height) ? null : Number(height),
      weight: blank(weight) ? null : Number(weight),
    });
    setSaved(true);
  });

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        void form.submit();
      }}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={t('profile.fields.name')}>
          <Input value={user.name ?? ''} disabled readOnly />
        </Field>
        <Field label={t('profile.fields.email')}>
          <Input value={user.email ?? ''} disabled readOnly />
        </Field>
        <Field label={t('profile.fields.nickname')}>
          <Input value={nickname} onChange={(event) => setNickname(event.target.value)} />
        </Field>
      </div>

      {roles.length > 0 && (
        <div className="border-line flex flex-col gap-2 border-t pt-5">
          <h3 className="text-ink text-sm font-semibold">{t('profile.details.rolesTitle')}</h3>
          <div className="flex flex-wrap gap-2">
            {roles.map((label) => (
              <Badge key={label} tone="outline">
                {label}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="border-line grid gap-5 border-t pt-5 sm:grid-cols-2">
        <p className="text-accent text-[13px] sm:col-span-2">{t('profile.privateFieldsHint')}</p>
        <Field label={t('profile.fields.gender')}>
          <Input type="number" value={gender} onChange={(event) => setGender(event.target.value)} />
        </Field>
        <Field label={t('profile.fields.birthdate')}>
          <Input
            type="date"
            value={birthdate}
            onChange={(event) => setBirthdate(event.target.value)}
          />
        </Field>
        <Field label={t('profile.fields.height')}>
          <Input type="number" value={height} onChange={(event) => setHeight(event.target.value)} />
        </Field>
        <Field label={t('profile.fields.weight')}>
          <Input
            type="number"
            step="0.1"
            value={weight}
            onChange={(event) => setWeight(event.target.value)}
          />
        </Field>
      </div>

      {saved && !form.pending && !form.error && <Alert tone="success">{t('profile.saved')}</Alert>}
      <SubmitError error={form.error} />

      <FormActions>
        <Button type="submit" disabled={form.pending}>
          {t('profile.save')}
        </Button>
      </FormActions>
    </form>
  );
}
